import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  Chip,
  Stack,
  Alert,
} from '@mui/material';
import {
  AutoAwesome,
  CloudUpload,
  Save,
} from '@mui/icons-material';
import EmbedAPIClient from '@embedapi/core';
import { ChantierService } from '../services/chantierService';
import { useAuthStore } from '../store/authStore';

const embedClient = new EmbedAPIClient('01xJgJ_bGEWTQjG6O2xsEuN6TqGk9LN0');

const ChantierCreatePage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [pastedText, setPastedText] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Form State (AI Generated)
  const [formData, setFormData] = useState<any>(null);

  const handleAIAutoCreate = async () => {
    if (!pastedText.trim()) return;
    setIsGenerating(true);
    setError(null);

    try {
      const systemPrompt = `You are a real estate project extractor. Given a text describing a construction project (Chantier), extract the following fields in valid JSON format:
{
  "name": "Project Name",
  "description": "Full description",
  "location": { "city": "City Name", "latitude": 33.5731, "longitude": -7.5898, "address": "Full address if available", "state": "Casablanca-Settat", "zipCode": "20000", "country": "Morocco" },
  "amenities": ["amenity1", "amenity2"],
  "progress": { "percentage": 0, "phase": "structural" }
}
Respond ONLY with the JSON object.`;

      const result = await embedClient.generate({
        service: 'openai',
        model: 'gpt-4o-mini',
        messages: [
          { role: 'system', content: systemPrompt },
          { role: 'user', content: pastedText }
        ],
      });

      const text = result?.choices?.[0]?.message?.content || result?.answer;
      if (!text) throw new Error("No response from AI");

      // Clean the text in case AI added markdown blocks
      const cleanJson = text.replace(/```json|```/g, '').trim();
      const data = JSON.parse(cleanJson);
      
      // Add unitTypesDetected for the UI review
      setFormData({
        ...data,
        unitTypesDetected: data.unitTypesDetected || ['Apartments', 'Studios']
      });
    } catch (err: any) {
      console.error('AI Generation error:', err);
      setError('Failed to extract data. Please try pasting the text again.');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleSave = async () => {
    if (!user || !formData) return;
    setIsSaving(true);

    try {
      await ChantierService.createChantier(formData, user.id);
      navigate('/promoteur');
    } catch (err) {
      console.error('Save error:', err);
      setError('Failed to save project to database.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Box>
      <Box sx={{ mb: 5 }}>
        <Typography variant="h3" sx={{ fontWeight: 800, fontFamily: '"Playfair Display", serif' }}>
          Launch New Chantier
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Use souk.immo AI to automatically build your project Master Page from a description.
        </Typography>
      </Box>

      <Grid container spacing={4}>
        {/* Left Side: Input */}
        <Grid item xs={12} md={formData ? 5 : 12}>
          <Card variant="outlined" sx={{ height: '100%' }}>
            <CardContent sx={{ p: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                <AutoAwesome color="primary" />
                <Typography variant="h6" sx={{ fontWeight: 700 }}>AI Auto-Creation</Typography>
              </Box>
              
              <Typography variant="body2" color="text.secondary" paragraph>
                Paste your project brochure text, marketing description, or technical specifications below. 
                Our AI will extract the title, location, unit types, and amenities for you.
              </Typography>

              <TextField
                fullWidth
                multiline
                rows={12}
                placeholder="Example: Marina Blanca is a new luxury development in Casablanca Finance City. It features 120 units including F3 apartments and Penthouses. Residents enjoy a rooftop pool, 24/7 security, and are just 10 mins from the beach..."
                value={pastedText}
                onChange={(e) => setPastedText(e.target.value)}
                variant="outlined"
                sx={{ bgcolor: 'white', mb: 3 }}
              />

              <Stack direction="row" spacing={2}>
                <Button 
                  variant="contained" 
                  size="large" 
                  fullWidth
                  onClick={handleAIAutoCreate}
                  disabled={isGenerating || !pastedText}
                  startIcon={isGenerating ? <CircularProgress size={20} color="inherit" /> : <AutoAwesome />}
                >
                  {isGenerating ? 'Analyzing...' : 'Generate Project Structure'}
                </Button>
                
                <Button 
                  variant="outlined" 
                  size="large" 
                  startIcon={<CloudUpload />}
                  disabled
                >
                  Upload PDF (Soon)
                </Button>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Right Side: AI Output Review */}
        {formData && (
          <Grid item xs={12} md={7}>
            <Card sx={{ border: '1px solid', borderColor: 'primary.main', height: '100%' }}>
              <CardContent sx={{ p: 4 }}>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 700 }}>Review Generated Project</Typography>
                <Divider sx={{ mb: 3 }} />

                {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <TextField 
                      fullWidth 
                      label="Project Name" 
                      value={formData.name} 
                      onChange={(e) => setFormData({...formData, name: e.target.value})} 
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField 
                      fullWidth 
                      multiline 
                      rows={3} 
                      label="Description" 
                      value={formData.description} 
                      onChange={(e) => setFormData({...formData, description: e.target.value})} 
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField 
                      fullWidth 
                      label="City" 
                      value={formData.location?.city} 
                      onChange={(e) => setFormData({...formData, location: {...formData.location, city: e.target.value}})} 
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField 
                      fullWidth 
                      label="Detected Unit Types" 
                      value={formData.unitTypesDetected?.join(', ')} 
                      helperText="AI detected these products"
                    />
                  </Grid>
                </Grid>

                <Box sx={{ mt: 4 }}>
                  <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 700 }}>Amenities Detected</Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {formData.amenities?.map((item: string) => (
                      <Chip key={item} label={item} color="primary" variant="outlined" />
                    ))}
                  </Box>
                </Box>

                <Box sx={{ mt: 5, display: 'flex', gap: 2 }}>
                  <Button 
                    variant="contained" 
                    size="large" 
                    fullWidth 
                    startIcon={isSaving ? <CircularProgress size={20} color="inherit" /> : <Save />}
                    onClick={handleSave}
                    disabled={isSaving}
                  >
                    Confirm & Publish Chantier
                  </Button>
                  <Button variant="text" color="inherit" onClick={() => setFormData(null)}>Cancel</Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default ChantierCreatePage;
